import { z } from 'zod';
export const JOB_REQUEST_STATUSES = [
    'new',
    'in_review',
    'matched',
    'notified',
    'closed',
];
// Privacy Policy / Terms version currently shown in the UI. Bump whenever the
// legal pages change materially — the value is persisted in
// job_requests.consent_version so we can prove which document the user
// accepted (Art. 7 GDPR).
export const PRIVACY_POLICY_VERSION = '2026-09-17';
// ---------------------------------------------------------------------------
// Rich-payload enums (wizard data model). The simple `jobRequestInputSchema`
// below stays backward-compatible for the legacy `/request` form; the rich
// schema layers the extra wizard fields on top.
// ---------------------------------------------------------------------------
export const CONTACT_TYPES = ['email', 'phone'];
export const EXPERIENCE_LEVELS = ['none', 'some', 'mid', 'senior'];
// Source of truth for experience-level copy across the wizard, profile, and
// admin surfaces. `title` is the short label (used in admin tables/filters);
// `subtitle` is the supporting line shown next to the title in richer UIs
// (wizard choice cards, profile details).
export const EXPERIENCE_LEVEL_LABELS = {
    none: { title: 'No experience', subtitle: "First logistics job — that's OK." },
    some: { title: 'Few months experience', subtitle: 'Less than 1 year total' },
    mid: { title: '1+ years experience', subtitle: 'Solid working history' },
    senior: { title: '2+ years experience', subtitle: 'Seasoned professional' },
};
export const CITIZENSHIP_STATUSES = [
    'citizen',
    'permanent',
    'work_visa',
    'other',
];
export const CITIZENSHIP_STATUS_LABELS = {
    citizen: 'Citizen',
    permanent: 'Permanent resident',
    work_visa: 'Work visa',
    other: 'Other / needs sponsorship',
};
export const JOB_CATEGORIES = [
    'transport',
    'warehouse',
    'specialists',
    'management',
];
export const JOB_CATEGORY_LABELS = {
    transport: 'Transport',
    warehouse: 'Warehouse',
    specialists: 'Specialists',
    management: 'Management',
};
// Canonical list of document kinds the wizard collects. Mirrors the wizard
// DocKey union (apps/client/src/components/landing/Wizard.tsx) plus a few
// document kinds tracked by admin even when not yet surfaced in the wizard
// (polizeiliches_fuehrungszeugnis, gesundheitszeugnis, sonstiges alias for
// `other`). Keep these in sync when the wizard catalogue changes.
export const DOCUMENT_KINDS = [
    'reisepass',
    'arbeitserlaubnis',
    'steuernummer',
    'sozialvers',
    'versicherung',
    'meldebescheinigung',
    'bankkarte',
    'immatrikulation',
    'fuehrerschein',
    'gesundheitszeugnis',
    'polizeiliches_fuehrungszeugnis',
    'sonstiges',
    'other',
];
export const LICENSE_CATEGORIES = [
    'AM',
    'A1',
    'A2',
    'A',
    'B',
    'BE',
    'C1',
    'C1E',
    'C',
    'CE',
    'D1',
    'D1E',
    'D',
    'DE',
    'L',
    'T',
];
export const JOB_ROLE_CATALOG = {
    transport: {
        titleKey: 'Transport & delivery',
        subKey: 'On the road',
        roles: [
            'Truck driver (C1/C1E/C/CE)',
            'Courier / delivery driver (cat. B)',
            'Cargo bike rider (no licence)',
        ],
    },
    warehouse: {
        titleKey: 'Warehouse work',
        subKey: 'Entry level',
        roles: [
            'Order picker',
            'Sorting, packing, dispatch',
            'Goods receiving / dispatch',
            'Forklift operator (with licence)',
            'Returns / inventory',
        ],
    },
    specialists: {
        titleKey: 'Warehouse & logistics specialists',
        subKey: 'Qualified roles',
        roles: [
            'Disposition',
            'Warehouse logistics specialist',
            'Senior storekeeper',
            'Quality control',
            'Dangerous goods officer',
        ],
    },
    management: {
        titleKey: 'Management & coordination',
        subKey: 'Leadership roles',
        roles: [
            'Warehouse manager',
            'Warehouse team lead',
            'Logistics manager',
            'Supply chain management',
            'Operations manager',
        ],
    },
};
// Labels for every DocumentKind. Wizard owns UI metadata (icons, images,
// subtype radios); only the label pair lives here.
export const DOCUMENT_CATALOG = {
    reisepass: { germanName: 'Reisepass', translationKey: 'Passport' },
    arbeitserlaubnis: { germanName: 'Arbeitserlaubnis', translationKey: 'Work permit' },
    steuernummer: { germanName: 'Steuernummer', translationKey: 'Tax ID' },
    sozialvers: { germanName: 'Sozialversicherungsnummer', translationKey: 'Social security number' },
    versicherung: { germanName: 'Krankenversicherung', translationKey: 'Health insurance' },
    meldebescheinigung: {
        germanName: 'Meldebescheinigung',
        translationKey: 'Registration certificate',
    },
    bankkarte: {
        germanName: 'Bankkarte auf denselben Namen',
        translationKey: 'Bank card under same name',
    },
    immatrikulation: {
        germanName: 'Immatrikulation Bescheinigung',
        translationKey: 'Enrollment certificate',
    },
    fuehrerschein: { germanName: 'Führerschein', translationKey: "Driver's license" },
    gesundheitszeugnis: { germanName: 'Gesundheitszeugnis', translationKey: 'Health certificate' },
    polizeiliches_fuehrungszeugnis: {
        germanName: 'Polizeiliches Führungszeugnis',
        translationKey: 'Police clearance certificate',
    },
    sonstiges: { germanName: 'Sonstiges Dokument', translationKey: 'Other document' },
    other: { germanName: 'Sonstiges Dokument', translationKey: 'Other document' },
};
// Display label per German licence category. Today these are identical to the
// code (e.g. "C+E" formatted from `CE`), but the indirection lets us tune the
// presentation (e.g. "C1+E") without touching every consumer.
export const LICENSE_CATEGORY_LABELS = {
    AM: 'AM',
    A1: 'A1',
    A2: 'A2',
    A: 'A',
    B: 'B',
    BE: 'B+E',
    C1: 'C1',
    C1E: 'C1+E',
    C: 'C',
    CE: 'C+E',
    D1: 'D1',
    D1E: 'D1+E',
    D: 'D',
    DE: 'D+E',
    L: 'L',
    T: 'T',
};
// ---------------------------------------------------------------------------
// Simple input schema (legacy /request form). Stays unchanged so RequestPage
// keeps working without touching the wizard rollout.
// ---------------------------------------------------------------------------
// Uses Zod's default `.strip()` behaviour (not `.strict()`): unknown keys on the
// POST body are silently dropped, which lets the handler inspect the raw `website`
// honeypot field and keeps the endpoint forward-compatible with newer clients.
// Legacy callers (early tests, anything still on the pre-024 contract) may
// submit a single `full_name`. Coerce it into first/last before strict
// validation so we don't break the wire contract during the rollout.
const splitLegacyFullName = (val) => {
    if (!val || typeof val !== 'object')
        return val;
    const obj = val;
    if (typeof obj.first_name === 'string' && obj.first_name.trim().length > 0)
        return obj;
    const fn = typeof obj.full_name === 'string' ? obj.full_name.trim() : '';
    if (!fn)
        return obj;
    const space = fn.indexOf(' ');
    const next = { ...obj };
    if (space === -1) {
        next.first_name = fn;
    }
    else {
        next.first_name = fn.slice(0, space);
        const rest = fn.slice(space + 1).trim();
        if (rest && typeof next.last_name !== 'string')
            next.last_name = rest;
    }
    return next;
};
// Object form — used internally by the rich schema via `.omit().extend()`.
// External callers should use `jobRequestInputSchema` (the preprocessed
// version), which also accepts a legacy `full_name` and splits it.
export const jobRequestInputSchemaObject = z.object({
    // Names are stored separately because Keycloak's user model is first/last
    // natively (the OIDC `given_name` / `family_name` claims) and splitting a
    // single field after the fact misclassifies names in many cultures. The
    // DB also persists `full_name` as a synced display column for legacy
    // surfaces; see migration 024.
    first_name: z.string().trim().min(1).max(120),
    last_name: z
        .string()
        .trim()
        .max(120)
        .optional()
        .transform((v) => {
        if (v === undefined)
            return undefined;
        return v.length === 0 ? undefined : v;
    }),
    email: z.string().trim().email().max(254),
    city: z.string().trim().min(1).max(120),
    comments: z
        .string()
        .max(2000)
        .optional()
        .transform((v) => {
        if (v === undefined)
            return undefined;
        const trimmed = v.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    }),
    consent_version: z.string().trim().min(1).max(64),
});
export const jobRequestInputSchema = z.preprocess(splitLegacyFullName, jobRequestInputSchemaObject);
// ---------------------------------------------------------------------------
// Rich-payload schemas — wizard maps directly to these.
// ---------------------------------------------------------------------------
const isoDateString = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const isoCountryCode = z
    .string()
    .trim()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'Expected 2-letter ISO country code (uppercase)');
export const jobSelectionSchema = z.object({
    category: z.enum(JOB_CATEGORIES),
    roles: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
});
export const requestDocumentSchema = z.object({
    kind: z.enum(DOCUMENT_KINDS),
    subtype: z.string().trim().min(1).max(80).optional(),
    expires_on: isoDateString.optional(),
    issuing_country: isoCountryCode.optional(),
    license_categories: z.array(z.enum(LICENSE_CATEGORIES)).max(LICENSE_CATEGORIES.length).optional(),
    first_registered_de: isoDateString.optional(),
    custom_names: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
});
// Address is sent as a nested object on the wire. The DB stores the three
// fields as flat columns (country_code, city, postal_address) so admin
// filters and indexes built in migration 008 keep working — the API mapper
// flattens on INSERT and re-nests on SELECT.
export const addressInputSchema = z.object({
    country_code: isoCountryCode,
    city: z.string().trim().min(1).max(120),
    postal_address: z
        .string()
        .max(500)
        .optional()
        .transform((v) => {
        if (v === undefined)
            return undefined;
        const trimmed = v.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    }),
});
// Email is OPTIONAL in the rich schema — the wizard contact step lets users
// supply either email OR phone (or both). A superRefine below enforces that at
// least one is present so the matcher can still reach them.
//
// The plain object form is shared with `jobRequestUpdateInputSchema` (the
// authenticated edit path), so the wizard payload is defined exactly once.
const jobRequestRichObjectSchema = jobRequestInputSchemaObject
    .omit({ email: true, city: true })
    .extend({
    address: addressInputSchema,
    email: z.string().trim().email().max(254).optional(),
    phone_e164: z
        .string()
        .trim()
        .min(1)
        .max(32)
        .regex(/^\+?[0-9]{6,31}$/, 'Expected E.164 phone (digits with optional leading +)')
        .optional(),
    phone_country: isoCountryCode.optional(),
    experience_level: z.enum(EXPERIENCE_LEVELS),
    // @deprecated — no longer collected by the wizard as of 2026-05.
    // The DB column and CITIZENSHIP_STATUSES enum remain so backfilled rows
    // and any future surfaces stay backwards-compatible.
    citizenship: z.enum(CITIZENSHIP_STATUSES).optional(),
    bio: z
        .string()
        .max(2000)
        .optional()
        .transform((v) => {
        if (v === undefined)
            return undefined;
        const trimmed = v.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    }),
    jobs: z.array(jobSelectionSchema).min(1).max(JOB_CATEGORIES.length),
    documents: z.array(requestDocumentSchema).max(50).default([]),
});
const requireContactChannel = (val, ctx) => {
    const hasEmail = typeof val.email === 'string' && val.email.length > 0;
    const hasPhone = typeof val.phone_e164 === 'string' && val.phone_e164.length > 0;
    if (!hasEmail && !hasPhone) {
        const msg = 'Provide at least one contact channel — email or phone';
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: msg });
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone_e164'], message: msg });
    }
};
// Mandatory documents — every job-seeker must claim at minimum a passport
// and a work permit (Aufenthaltstitel / Arbeitserlaubnis). The client
// wizard already gates the Documents step on these, but the rule is a
// domain invariant so the server enforces it independently — a direct
// POST to /api/job-requests cannot bypass it.
const requireMandatoryDocuments = (val, ctx) => {
    const claimedKinds = new Set((val.documents ?? []).map((d) => d.kind));
    for (const required of REQUIRED_DOCUMENT_KINDS) {
        if (!claimedKinds.has(required)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['documents'],
                message: `Missing required document: ${required}`,
            });
        }
    }
};
export const jobRequestRichInputSchema = z.preprocess(splitLegacyFullName, jobRequestRichObjectSchema.superRefine((val, ctx) => {
    requireContactChannel(val, ctx);
    requireMandatoryDocuments(val, ctx);
}));
// Body of PUT /api/me/job-request — the authenticated edit / first-time
// insert path. Identity is the JWT's email, so `email` is not accepted (a
// client-supplied value is stripped). Consent was captured at signup; it is
// optional here and only required by the server on the insert path.
export const jobRequestUpdateInputSchema = jobRequestRichObjectSchema
    .omit({ email: true, consent_version: true })
    .extend({
    consent_version: z.string().trim().min(1).max(64).optional(),
})
    .superRefine(requireMandatoryDocuments);
// Documents every job-seeker must claim before a profile is considered
// complete. Single source of truth — the wizard's `required: true` flag in
// DOC_DEFS mirrors this list, and the API enforces it via the schema above
// so callers that skip the wizard still get a 400.
export const REQUIRED_DOCUMENT_KINDS = ['reisepass', 'arbeitserlaubnis'];
// ---------------------------------------------------------------------------
// Admin list query schema. Designed to consume URLSearchParams strings, so all
// scalar fields go through `z.coerce` / enums. page/pageSize default to sane
// values; sortBy is enum-bound — the API repository allowlists it again before
// interpolating into SQL.
// ---------------------------------------------------------------------------
export const JOB_REQUEST_SORT_FIELDS = [
    'created_at',
    'updated_at',
    'status',
    'country_code',
    'experience_level',
];
export const jobRequestListQuerySchema = z.object({
    status: z.enum(JOB_REQUEST_STATUSES).optional(),
    // Rich-schema filter: matches rows whose `jobs` JSONB contains an entry
    // with `category === <category>` and (if `role` is set) that role within
    // its `roles` array. Mirrors the wizard's 4-category taxonomy 1:1.
    category: z.enum(JOB_CATEGORIES).optional(),
    role: z
        .string()
        .trim()
        .max(120)
        .optional()
        .transform((v) => (v && v.length > 0 ? v : undefined)),
    country_code: isoCountryCode.optional(),
    experience_level: z.enum(EXPERIENCE_LEVELS).optional(),
    citizenship: z.enum(CITIZENSHIP_STATUSES).optional(),
    q: z
        .string()
        .trim()
        .max(120)
        .optional()
        .transform((v) => (v && v.length > 0 ? v : undefined)),
    // Free-text match against city OR postal_address (case-insensitive).
    // Indexed by gin_trgm on city / btree(lower(city)) — admin uses this for
    // location-based triage without needing the row's exact spelling.
    location: z
        .string()
        .trim()
        .max(120)
        .optional()
        .transform((v) => (v && v.length > 0 ? v : undefined)),
    page: z.coerce.number().int().min(1).max(10_000).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(25),
    sortBy: z.enum(JOB_REQUEST_SORT_FIELDS).optional().default('created_at'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});
//# sourceMappingURL=jobRequest.js.map